import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiCheckCircle,
  FiFileText,
  FiZap,
  FiRefreshCw,
  FiLink,
} from "react-icons/fi";
import HeaderFirst from "utils/HeaderFirst";
import { useGetRapprochementPiecesQuery } from "actions/pieces/queries";
import {
  useComptabiliserPieceMutation,
  useRattacherPieceMutation,
} from "actions/pieces/mutations";
import "../fiscalite/fiscalite.css";

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

const PAGE_SIZE = 15;

const Pager = ({ total, page, setPage }) => {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (pages <= 1) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderTop: "1px solid #e2e8f0", fontSize: 12 }}>
      <span className="text-muted">{total} élément(s) : Page {page}/{pages}</span>
      <div style={{ display: "flex", gap: 6 }}>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ borderRadius: "31px" }}>
          ← Précédent
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= pages} onClick={() => setPage(page + 1)} style={{ borderRadius: "31px" }}>
          Suivant →
        </button>
      </div>
    </div>
  );
};

const PillBtn = ({ color, onClick, children }) => (
  <button
    type="button"
    className="btn btn-outline-secondary"
    onClick={onClick}
    style={{
      borderRadius: "31px",
      padding: "4px 14px",
      fontSize: 12,
      borderColor: color,
      color,
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </button>
);

const RapprochementPage = () => {
  const { clientID } = useParams();
  const dossier_id = clientID;
  const { data, isLoading, refetch } = useGetRapprochementPiecesQuery({ dossier_id });
  const [comptabiliserPiece] = useComptabiliserPieceMutation();
  const [rattacherPiece] = useRattacherPieceMutation();
  const [attachTarget, setAttachTarget] = useState(null);
  const [tab, setTab] = useState("matched");
  const [page, setPage] = useState(1);
  const paginate = (items) => (items || []).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleComptabiliser = async (p) => {
    try {
      await comptabiliserPiece({ piece_id: p.id, dossier_id }).unwrap();
      toast.success(`« ${p.titre} » comptabilisée.`);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Impossible de comptabiliser cette pièce.");
    }
  };

  const handleRattacher = async (piece) => {
    if (!attachTarget) return;
    try {
      await rattacherPiece({
        piece_id: piece.id,
        target_type: "ecriture",
        target_id: attachTarget.id,
        dossier_id,
      }).unwrap();
      toast.success(`« ${piece.titre} » rattachée à l'écriture.`);
      setAttachTarget(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Impossible de rattacher la pièce.");
    }
  };

  const stats = data?.stats || {};
  const total =
    (stats.matched_count || 0) +
    (stats.pieces_orphelines_count || 0) +
    (stats.ecritures_orphelines_count || 0) +
    (stats.doublons_count || 0);

  const tabs = [
    { key: "matched", label: "Rapprochées", count: stats.matched_count, cls: "fisc-tab-paid" },
    { key: "pieces", label: "Pièces sans écriture", count: stats.pieces_orphelines_count, cls: "fisc-tab-pending" },
    { key: "ecritures", label: "Écritures sans pièce", count: stats.ecritures_orphelines_count, cls: "fisc-tab-urgent" },
    { key: "doublons", label: "Doublons suspects", count: stats.doublons_count, cls: "fisc-tab-dark" },
  ];

  const nom = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <h4 className="titre-dossier">
        <FiLink style={{ marginRight: 8, color: "var(--sidebar-green, #009688)" }} />
        Rapprochement pièces / écritures
      </h4>
      <span className="fisc-badge fisc-badge-success">GED ↔ Journal</span>
      <span className="nbrDossier">{total}</span>
    </div>
  );

  return (
    <div
      style={{
        backgroundColor: "white",
        minHeight: "500px",
        borderRadius: "20px",
        padding: "15px",
      }}
    >
      <div className="d-flex align-items-center flex-wrap pb-3">
        <HeaderFirst
          nom={nom}
          description={
            <div>
              Contrôle de cohérence : chaque écriture comptable doit avoir son
              justificatif, chaque pièce sa comptabilisation.
            </div>
          }
        />
      </div>

      <div className="fisc-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`fisc-tab ${t.cls} ${tab === t.key ? "active" : ""}`}
            onClick={() => { setTab(t.key); setPage(1); }}
          >
            {t.label}
            <span className="fisc-tab-count">{t.count || 0}</span>
          </button>
        ))}
      </div>
      <span className="fisc-tabs-sep" aria-hidden="true" />

      {isLoading ? (
        <div className="fisc-empty">
          <FiRefreshCw size={40} />
          <p className="mt-2 mb-1">Analyse du rapprochement en cours…</p>
        </div>
      ) : tab === "matched" ? (
        (data?.matched || []).length === 0 ? (
          <div className="fisc-empty">
            <FiCheckCircle size={40} />
            <p className="mt-2 mb-1">Aucune pièce rapprochée.</p>
          </div>
        ) : (
          <div className="fisc-list">
            <div className="fisc-list-head">
              <span>
                <FiCheckCircle style={{ marginRight: 6, color: "var(--sidebar-green, #009688)" }} />
                Pièces rapprochées ({data.matched.length})
              </span>
            </div>
            {paginate(data.matched).map((m, i) => (
              <div key={i} className="fisc-row" style={{ fontSize: 13, display: "grid", gridTemplateColumns: "1fr 24px 1fr", alignItems: "center", gap: 8 }}>
                <span>
                  <strong>{m.piece.titre}</strong>
                  <span className="text-muted"> : {m.piece.partenaire || "N/A"}, {fmt(m.piece.montant)} FCFA, {m.piece.date || ":"}</span>
                </span>
                <span style={{ color: "#166534", textAlign: "center" }}>↔</span>
                <span>
                  {m.ecriture.libelle}
                  <span className="text-muted"> : {fmt(m.ecriture.montant)} FCFA, {m.ecriture.date || ":"}</span>
                </span>
              </div>
            ))}
            <Pager total={data.matched.length} page={page} setPage={setPage} />
          </div>
        )
      ) : tab === "pieces" ? (
        (data?.pieces_orphelines || []).length === 0 ? (
          <div className="fisc-empty">
            <FiFileText size={40} />
            <p className="mt-2 mb-1">Aucune pièce orpheline.</p>
          </div>
        ) : (
          <div className="fisc-list">
            <div className="fisc-list-head">
              <span>
                <FiFileText style={{ marginRight: 6, color: "#92400e" }} />
                Pièces sans écriture ({data.pieces_orphelines.length})
              </span>
            </div>
            {paginate(data.pieces_orphelines).map((p, i) => (
              <div key={i} className="fisc-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
                <span>
                  <strong>{p.titre}</strong>
                  <span className="text-muted">
                    {" "}: {p.partenaire || "N/A"}, {fmt(p.montant)} FCFA, {p.date || ":"}
                  </span>
                  <span style={{ color: "#92400e", marginLeft: 8, fontSize: 12 }}>⚠ non comptabilisée</span>
                </span>
                <PillBtn color="#92400e" onClick={() => handleComptabiliser(p)}>
                  Comptabiliser
                </PillBtn>
              </div>
            ))}
            <Pager total={data.pieces_orphelines.length} page={page} setPage={setPage} />
          </div>
        )
      ) : tab === "ecritures" ? (
        (data?.ecritures_orphelines || []).length === 0 ? (
          <div className="fisc-empty">
            <FiFileText size={40} />
            <p className="mt-2 mb-1">Aucune écriture orpheline.</p>
          </div>
        ) : (
          <div className="fisc-list">
            <div className="fisc-list-head">
              <span>
                <FiFileText style={{ marginRight: 6, color: "#991b1b" }} />
                Écritures sans justificatif ({data.ecritures_orphelines.length})
              </span>
            </div>
            {paginate(data.ecritures_orphelines).map((e, i) => (
              <div key={i} className="fisc-row" style={{ fontSize: 13 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>
                    <strong>{e.libelle}</strong>
                    <span className="text-muted">
                      {" "}: {fmt(e.montant)} FCFA, {e.date || ":"}
                    </span>
                    <span style={{ color: "#991b1b", marginLeft: 8, fontSize: 12 }}>⚠ pièce manquante</span>
                  </span>
                  <PillBtn color="#991b1b" onClick={() => setAttachTarget(attachTarget?.id === e.id ? null : e)}>
                    {attachTarget?.id === e.id ? "Annuler" : "Attacher"}
                  </PillBtn>
                </div>
                {attachTarget?.id === e.id && (
                  <div style={{ marginTop: 8, padding: 10, background: "#fef2f2", borderRadius: 8, border: "1px solid #fecaca" }}>
                    {(data.pieces_orphelines || []).length === 0 ? (
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Aucune pièce disponible, importez d&apos;abord le justificatif.
                      </span>
                    ) : (
                      data.pieces_orphelines.map((p, j) => (
                        <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", fontSize: 12 }}>
                          <span>{p.titre} : {fmt(p.montant)} FCFA</span>
                          <PillBtn color="#991b1b" onClick={() => handleRattacher(p)}>
                            Choisir
                          </PillBtn>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
            <Pager total={data.ecritures_orphelines.length} page={page} setPage={setPage} />
          </div>
        )
      ) : (
        (data?.doublons || []).length === 0 ? (
          <div className="fisc-empty">
            <FiZap size={40} />
            <p className="mt-2 mb-1">Aucun doublon suspect.</p>
          </div>
        ) : (
          <div className="fisc-list">
            <div className="fisc-list-head">
              <span>
                <FiZap style={{ marginRight: 6, color: "#7f1d1d" }} />
                Doublons suspects ({data.doublons.length})
              </span>
            </div>
            {paginate(data.doublons).map((d, i) => (
              <div key={i} className="fisc-row" style={{ fontSize: 13 }}>
                {[d.ecriture_1, d.ecriture_2].filter(Boolean).map((ec, j) => (
                  <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>
                      <strong>{ec.libelle}</strong>
                      <span className="text-muted">
                        {" "}: {fmt(ec.montant)} FCFA, {ec.date || ":"} <em>(#{ec.id})</em>
                      </span>
                    </span>
                    {j === 0 && (
                      <span style={{ color: "#7f1d1d", fontSize: 12, fontWeight: 600 }}>⚠ doublon possible</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
            <Pager total={data.doublons.length} page={page} setPage={setPage} />
          </div>
        )
      )}
    </div>
  );
};

export default RapprochementPage;
